import { useDashToast as useToast } from '@dashhudson/dashing-ui/toast';
import { v4 as uuidv4 } from 'uuid';

import {
  COMMUNITY_INTERACTION_ACTIONS,
  COMMUNITY_INTERACTION_ACTIONS_ERROR_TOASTS,
  COMMUNITY_INTERACTION_PLATFORM_BY_TYPE,
  COMMUNITY_INTERACTION_TYPES,
} from '@/app/community/constants';
import { getToastForInteractionType } from '@/app/community/utils/community-interactions';
import { useAuthStore } from '@/stores/auth';
import { useCommunityCommentsStore } from '@/stores/community-comments';
import { useCommunityDmsStore } from '@/stores/community-dms';
import { useCommunityInteractionStore } from '@/stores/community-interactions';
import { useCommunityTranslationStore } from '@/stores/community-translation';

export function useReplyHandler(interaction) {
  const { addToast } = useToast({ closable: true, life: 3000 });
  const commentsStore = useCommunityCommentsStore();
  const dmsStore = useCommunityDmsStore();
  const translationStore = useCommunityTranslationStore();
  const interactionStore = useCommunityInteractionStore();
  const authStore = useAuthStore();

  const submitReply = async (text, isTranslated = false) => {
    const typeKey = interaction.type;
    const platform = COMMUNITY_INTERACTION_PLATFORM_BY_TYPE[typeKey].platform.toLowerCase();

    const result = { data: null, error: null };

    switch (typeKey) {
      case COMMUNITY_INTERACTION_TYPES.INSTAGRAM_CONVERSATION:
      case COMMUNITY_INTERACTION_TYPES.TWITTER_CONVERSATION:
      case COMMUNITY_INTERACTION_TYPES.FACEBOOK_CONVERSATION: {
        try {
          result.data = await dmsStore.createPlatformMessage({
            platform,
            conversation: interaction,
            data: { uuid: uuidv4(), text },
            isTranslated,
          });
        } catch (err) {
          result.error = err;
          addToast({
            severity: 'error',
            message: COMMUNITY_INTERACTION_ACTIONS_ERROR_TOASTS.POSTED,
          });
        }
        return result;
      }

      case COMMUNITY_INTERACTION_TYPES.INSTAGRAM_COMMENT: {
        try {
          const parentId =
            interaction.parentId ??
            interaction.instagramParentCommentId ??
            interaction.instagramCommentId;
          const res = await commentsStore.createComment({
            platform,
            data: {
              brand_id: interaction.brandId,
              text,
              parent_comment_id: parentId,
            },
            interaction,
            isTranslated,
            isGdi: true,
          });
          result.data = res.data;
          addToast({
            severity: 'success',
            message: getToastForInteractionType(typeKey, COMMUNITY_INTERACTION_ACTIONS.POSTED),
          });
        } catch (err) {
          result.error = err;
          addToast({
            severity: 'error',
            message: COMMUNITY_INTERACTION_ACTIONS_ERROR_TOASTS.POSTED,
          });
        }
        return result;
      }

      case COMMUNITY_INTERACTION_TYPES.FACEBOOK_COMMENT: {
        const localId = Math.floor(Math.random() * 1e6) + 1;
        const nowIso = new Date().toISOString().replace('Z', '');
        const optimisticReply = {
          id: localId,
          postPending: true,
          facebookCommentId: localId,
          attachment: null,
          facebookUsername:
            interactionStore.selectedCommunityInteractionMedia?.sourceData?.pageDetails?.name,
          facebookProfilePictureUrl: authStore.currentBrand?.avatarUrl,
          brandId: interaction.brandId,
          type: COMMUNITY_INTERACTION_TYPES.FACEBOOK_COMMENT,
          isOwned: true,
          isRead: true,
          text,
          sourceUpdatedAt: nowIso,
          createdAt: nowIso,
          updatedAt: nowIso,
        };
        commentsStore.updateCommentDetailReplies({ reply: optimisticReply });

        try {
          const res = await commentsStore.createComment({
            platform,
            data: {
              brand_id: interaction.brandId,
              text,
              parent_comment_id:
                interaction.parentId ??
                interaction.facebookParentCommentId ??
                interaction.facebookCommentId,
            },
            interaction,
            updateCommentDetailReplies: false,
            isGdi: true,
          });
          optimisticReply.postPending = false;
          commentsStore.replaceCommentDetailReply({
            replyId: localId,
            newReply: res.data,
          });
          if (isTranslated) {
            translationStore.updateTranslationMap(res.data.id);
          }
          result.data = res.data;
          addToast({
            severity: 'success',
            message: getToastForInteractionType(typeKey, COMMUNITY_INTERACTION_ACTIONS.POSTED),
          });
        } catch (err) {
          addToast({
            severity: 'error',
            message: COMMUNITY_INTERACTION_ACTIONS_ERROR_TOASTS.POSTED,
          });
          commentsStore.updateCommentDetailReplies({
            reply: optimisticReply,
            add: false,
          });
          result.error = err;
        }
        return result;
      }

      case COMMUNITY_INTERACTION_TYPES.TIKTOK_COMMENT:
      case COMMUNITY_INTERACTION_TYPES.TIKTOK_COMMENT_MENTION: {
        try {
          const res = await commentsStore.createComment({
            platform,
            data: {
              text,
              brand_id: interaction.brandId,
              parent_comment_id: interaction.tiktokCommentId,
            },
            interaction,
            isTranslated,
            isGdi: true,
          });
          result.data = res.data.id;
          addToast({
            severity: 'success',
            message: getToastForInteractionType(typeKey, COMMUNITY_INTERACTION_ACTIONS.POSTED),
          });
        } catch (err) {
          addToast({
            severity: 'error',
            message: COMMUNITY_INTERACTION_ACTIONS_ERROR_TOASTS.POSTED,
          });
          result.error = err;
        }
        return result;
      }

      case COMMUNITY_INTERACTION_TYPES.TWITTER_MENTION:
      case COMMUNITY_INTERACTION_TYPES.TWITTER_RETWEET:
      case COMMUNITY_INTERACTION_TYPES.TWITTER_QUOTE: {
        let res;
        try {
          res = await commentsStore.createComment({
            platform: 'twitter',
            data: {
              brandId: interaction.brandId,
              parentTweetId: interaction.twitterTweetId,
              text,
              isQuote: false,
            },
            interaction,
            isTranslated,
            isGdi: true,
          });
          result.data = res.data;
          addToast({
            severity: 'success',
            message: getToastForInteractionType(typeKey, COMMUNITY_INTERACTION_ACTIONS.POSTED),
          });
        } catch (err) {
          const apiErrorMessage = err.response?.data?.errors?.application?.reason;
          addToast({
            severity: 'error',
            message: apiErrorMessage,
          });
          result.error = err;
        }
        return result;
      }

      case COMMUNITY_INTERACTION_TYPES.YOUTUBE_COMMENT: {
        let res;
        try {
          res = await commentsStore.createComment({
            platform,
            data: {
              brandId: interaction.brandId,
              parentCommentId: interaction.youtubeCommentId,
              text,
            },
            interaction,
            isTranslated,
            isGdi: true,
          });
          result.data = res.data;
          addToast({
            severity: 'success',
            message: getToastForInteractionType(typeKey, COMMUNITY_INTERACTION_ACTIONS.POSTED),
          });
        } catch (err) {
          const apiErrorMessage = err.response?.data?.errors?.application?.reason;
          addToast({
            severity: 'error',
            message: apiErrorMessage,
          });
          result.error = err;
        }
        return result;
      }

      default:
        addToast({
          severity: 'error',
          message: 'Something went wrong. Please try again',
        });
        return {
          data: null,
          error: 'Unknown interaction type',
        };
    }
  };

  return { submitReply };
}
